import { getVendorIds } from '@/helpers';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { getCalendarEventsList } from '@/api';
import { aggregateDeliveryEventVendors, getEventPreview } from '../Dashboard.page/helpers';
import { DateEvents, EventPreview } from '../Dashboard.page/types';
import { useEffect, useState } from 'react';
import { EventTypeFilter } from './types';
import { getFilteredEvents } from './helpers';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const useCalendarEventList = ({
  selectedDate,
  maxDate,
  selectedVendorIds,
  selectedEventType,
}: {
  selectedDate: string;
  maxDate: string;
  selectedVendorIds: string[];
  selectedEventType: EventTypeFilter[];
}) => {
  const vendorIds = getVendorIds();
  const maxDateBefore = dayjs(maxDate).endOf('day').format();
  const [startTimeAfter, setStartTimeAfter] = useState<string>();
  const [startTimeBefore, setStartTimeBefore] = useState<string>();
  const [scrolledIntoSelectDay, setScrolledIntoSelectDay] = useState(false);
  useEffect(() => {
    setScrolledIntoSelectDay(false);
    if (
      dayjs(selectedDate).isSameOrAfter(startTimeAfter) &&
      dayjs(selectedDate).isSameOrBefore(startTimeBefore)
    ) {
      return;
    }

    setEventsByDate([]);
    const currentDay = dayjs(selectedDate).day();
    setStartTimeAfter(dayjs(selectedDate).subtract(currentDay, 'day').startOf('day').format());
    const startTime = dayjs(selectedDate)
      .add(currentDay > 6 ? currentDay : 6 - currentDay, 'day')
      .endOf('day')
      .format();
    setStartTimeBefore(dayjs(startTime).isAfter(maxDateBefore) ? maxDateBefore : startTime);
  }, [selectedDate]);

  const { data, loading, networkStatus } = useQuery(getCalendarEventsList, {
    variables: {
      vendorIds,
      startTimeBefore,
      startTimeAfter,
    },
    skip: vendorIds.length === 0 || !startTimeBefore || !startTimeAfter,
    notifyOnNetworkStatusChange: true,
  });

  const [eventsByDate, setEventsByDate] = useState<DateEvents[]>([]);

  useEffect(() => {
    if (loading) return;

    const nodes = data?.searchEventVendors?.eventVendors?.nodes || [];
    const filteredEvents = getFilteredEvents(nodes, selectedVendorIds, selectedEventType).filter(
      (event) => event !== null
    );
    if (filteredEvents.length > 0) {
      const previewEvents = getEventPreview(
        aggregateDeliveryEventVendors(filteredEvents),
        false,
        true
      );

      const events = previewEvents.toSorted(
        (a, b) => dayjs(a.eventTime).valueOf() - dayjs(b.eventTime).valueOf()
      );

      const eventsByDateMap = new Map<string, EventPreview[]>();

      events.forEach((event: EventPreview) => {
        const date = dayjs(event.eventTime).format('YYYY-MM-DD');
        const existingEvents = eventsByDateMap.get(date);

        if (existingEvents) {
          existingEvents.push(event);
        } else {
          eventsByDateMap.set(date, [event]);
        }
      });

      const newEventsByDate: DateEvents[] = Array.from(eventsByDateMap.entries())
        .map(([date, events]) => ({ date, events }))
        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

      setEventsByDate(newEventsByDate);
    }
  }, [data, loading, selectedEventType, selectedVendorIds]);

  const showEventList = eventsByDate.length;

  const isRefetching = networkStatus === 4;

  return {
    eventsByDate,
    loading,
    isRefetching,
    showEventList,
    startDate: startTimeAfter,
    endDate: startTimeBefore,
    scrolledIntoSelectDay,
    setScrolledIntoSelectDay,
  };
};
